import React from "react";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SearchBar from "../../../shared/components/SearchBar";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TimelineIcon from "@mui/icons-material/Timeline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DateRangeIcon from "@mui/icons-material/DateRange";
import LanguageIcon from "@mui/icons-material/Language";
import {
  ACTIONS_HEADER_BTN_SX,
  ACTION_MENU_PAPER_SX,
} from "../../../theme/buttonstyles";

const BRAND_BLUE = "#00336C";

const DATE_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    minHeight: 42,
    fontSize: "0.92rem",
    color: BRAND_BLUE,
    borderRadius: 3,
    background: "linear-gradient(180deg, rgba(231,240,218,0.25) 0%, #ffffff 80%)",
    boxShadow: "0 4px 14px rgba(0, 51, 108, 0.08)",
    "& fieldset": { border: "2px solid #00336C" },
    "&:hover fieldset": { borderColor: "#00336C" },
    "&.Mui-focused fieldset": { borderColor: "#00336C", borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { color: BRAND_BLUE, fontWeight: 700 },
  "& input": { color: `${BRAND_BLUE} !important`, WebkitTextFillColor: BRAND_BLUE, fontWeight: 700 },
  "& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionContent": {
    color: `${BRAND_BLUE} !important`,
  },
  "& .MuiPickersInputBase-sectionBefore, & .MuiPickersInputBase-sectionAfter": {
    color: `${BRAND_BLUE} !important`,
  },
  "& .MuiIconButton-root, & .MuiSvgIcon-root": { color: BRAND_BLUE },
};

const PICKER_POPPER_PROPS = {
  sx: {
    "& .MuiPickersLayout-root": { borderRadius: 4 },
    "& .MuiPickersToolbar-root": {
      backgroundColor: BRAND_BLUE,
      color: "#fff",
      "& .MuiTypography-root": { color: "#fff" },
      "& .MuiPickersToolbarButton-root": { color: "#fff" },
    },
    "& .MuiTabs-root": {
      backgroundColor: BRAND_BLUE,
      "& .MuiTab-root": { color: "rgba(255,255,255,0.7)" },
      "& .Mui-selected": { color: "#fff" },
      "& .MuiTabs-indicator": { backgroundColor: "#fff" },
    },
  },
};

export default function WebsiteVisitsHeader({
  q,
  setQ,
  navigate,
  selectedCount,
  handleSaveSelectedVisit,
  handleSaveSelectedBRE,
  handleExportSelected,
  loading,
  canAccessBRE,
  since,
  until,
  onSinceChange,
  onUntilChange,
}) {
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState(null);
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const [dateAnchorEl, setDateAnchorEl] = React.useState(null);
  const dateMenuOpen = Boolean(dateAnchorEl);

  const handleOpenActionsMenu = (event) => setActionsAnchorEl(event.currentTarget);
  const handleCloseActionsMenu = () => setActionsAnchorEl(null);

  const handleOpenDateMenu = (event) => setDateAnchorEl(event.currentTarget);
  const handleCloseDateMenu = () => setDateAnchorEl(null);

  const sinceLabel = since ? dayjs(since).format("MMM D") : null;
  const untilLabel = until ? dayjs(until).format("MMM D, YYYY") : null;
  const dateLabel = sinceLabel && untilLabel ? `${sinceLabel} – ${untilLabel}` : "Date Range";

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        borderBottom: "1px solid #dce4ef",
        background: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: { xs: 2, md: 3 },
          py: 1.75,
          flexWrap: "wrap",
        }}
      >
        {/* Left: icon + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              background: "#648315",
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            <LanguageIcon sx={{ fontSize: 19 }} />
          </Box>
          <Typography
            sx={{
              fontSize: "1.05rem",
              fontWeight: 800,
              color: BRAND_BLUE,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            Website Visitors
          </Typography>
        </Box>

        {/* Center: search bar */}
        <Box sx={{ flexGrow: 1, minWidth: 0, width: { xs: "100%", md: "auto" } }}>
          <SearchBar
            value={q}
            onChange={setQ}
            placeholder="Search Company, City, State, Country, Sector, or NAICS..."
            fullWidth
          />
        </Box>

        {/* Right: date range + actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {/* Date range filter */}
          <Button
            startIcon={<DateRangeIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleOpenDateMenu}
            sx={{
              ...ACTIONS_HEADER_BTN_SX,
              minWidth: 0,
              px: 2,
              fontSize: 12,
              color: BRAND_BLUE,
              borderColor: BRAND_BLUE,
              minHeight: 42,
              height: 42,
            }}
          >
            {dateLabel}
          </Button>

          <Menu
            anchorEl={dateAnchorEl}
            open={dateMenuOpen}
            onClose={handleCloseDateMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                border: "1px solid rgba(0,51,108,0.15)",
                borderRadius: 3,
                boxShadow: "0 20px 60px rgba(15,23,42,0.22), 0 4px 16px rgba(15,23,42,0.1)",
                p: 1.5,
                background: "#ffffff",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                minWidth: 280,
              },
            }}
          >
            <Box
              onKeyDown={(e) => e.stopPropagation()}
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Since"
                  value={since ? dayjs(since) : null}
                  onChange={(value) => onSinceChange(value ? dayjs(value).toISOString() : "")}
                  sx={DATE_FIELD_SX}
                  slotProps={{
                    textField: { size: "small", InputLabelProps: { shrink: true } },
                    popper: PICKER_POPPER_PROPS,
                  }}
                />
                <DateTimePicker
                  label="Until"
                  value={until ? dayjs(until) : null}
                  onChange={(value) => onUntilChange(value ? dayjs(value).toISOString() : "")}
                  sx={DATE_FIELD_SX}
                  slotProps={{
                    textField: { size: "small", InputLabelProps: { shrink: true } },
                    popper: PICKER_POPPER_PROPS,
                  }}
                />
              </LocalizationProvider>
              <Button
                size="small"
                onClick={() => { onSinceChange(""); onUntilChange(""); }}
                sx={{ color: BRAND_BLUE, fontWeight: 700, textTransform: "none", alignSelf: "flex-end" }}
              >
                Reset
              </Button>
            </Box>
          </Menu>

          {/* Actions menu */}
          <Button
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleOpenActionsMenu}
            sx={ACTIONS_HEADER_BTN_SX}
          >
            Actions
          </Button>

          <Menu
            anchorEl={actionsAnchorEl}
            open={actionsMenuOpen}
            onClose={handleCloseActionsMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: ACTION_MENU_PAPER_SX }}
          >
            <MenuItem
              onClick={() => { handleCloseActionsMenu(); handleSaveSelectedVisit(); }}
              disabled={selectedCount === 0}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "#0F766E" }}>
                <SaveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Save to Prospect Companies</ListItemText>
            </MenuItem>

            {canAccessBRE && (
              <MenuItem
                onClick={() => { handleCloseActionsMenu(); handleSaveSelectedBRE(); }}
                disabled={selectedCount === 0}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "#0F766E" }}>
                  <SaveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Save to BR&E Companies</ListItemText>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => { handleCloseActionsMenu(); navigate("/website-metrics"); }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "#0F766E" }}>
                <TimelineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Website Insights</ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => { handleCloseActionsMenu(); handleExportSelected(); }}
              disabled={selectedCount === 0}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "#0284C7" }}>
                <FileDownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{`Export Selected (${selectedCount})`}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {loading && <LinearProgress />}
    </Box>
  );
}
